/**
 * @file spacing.ts
 * @description Centralized spacing constants following 4px/8px grid system
 *
 * All spacing values use Tailwind's default spacing scale where:
 * - 1 unit = 4px (0.25rem)
 * - 2 units = 8px (0.5rem)
 * - 4 units = 16px (1rem)
 * - 8 units = 32px (2rem)
 * - 12 units = 48px (3rem)
 * - 16 units = 64px (4rem)
 * - 20 units = 80px (5rem)
 *
 * @architecture POLISH-01 Visual Consistency
 */

/**
 * Spacing constants for consistent layout throughout the application.
 * All values follow the 4px/8px grid system for visual harmony.
 */
export const SPACING = {
  /** Content rails and major sections */
  rails: {
    /** Vertical spacing between content rails (48px) */
    gap: "space-y-12",
    /** Section padding for vertical spacing (64px) */
    sectionPadding: "py-16",
    /** Bottom padding for pages (80px) */
    pageBottom: "pb-20",
  },

  /** Card and grid layouts */
  cards: {
    /** Standard gap between cards in rails (16px) */
    gap: "gap-4",
    /** Large gap between cards or sections (32px) */
    gapLarge: "gap-8",
    /** Tight gap for compact layouts (8px) */
    gapTight: "gap-2",
  },

  /** Form elements */
  forms: {
    /** Standard input padding (16px x 8px) */
    inputPadding: "px-4 py-2",
    /** Vertical spacing between form fields (16px) */
    fieldSpacing: "space-y-4",
    /** Vertical spacing between form sections (48px) */
    sectionSpacing: "space-y-12",
  },

  /** Badges, tags, and labels */
  badges: {
    /** Standard badge padding (8px x 4px) */
    padding: "px-2 py-1",
    /** Small badge padding for tight spaces (6px x 2px) */
    paddingSmall: "px-1.5 py-0.5",
  },

  /** Buttons */
  buttons: {
    /** Small button padding (16px x 8px) */
    small: "px-4 py-2",
    /** Medium button padding (24px x 12px) */
    medium: "px-6 py-3",
    /** Large button padding (32px x 16px) */
    large: "px-8 py-4",
  },

  /** Player and video controls */
  player: {
    /** Spacing between control groups (16px) */
    controlSpacing: "space-y-4",
    /** Padding around control bar (16px) */
    controlBarPadding: "p-4",
  },

  /** Navigation and menus */
  navigation: {
    /** Spacing between nav items (8px) */
    itemSpacing: "gap-2",
    /** Padding for nav items (8px x 4px) */
    itemPadding: "px-2 py-1",
  },

  /** Search and filters */
  search: {
    /** Input padding for search bars (48px left for icon, 16px right, 16px vertical) */
    inputPadding: "py-4",
    /** Results grid gap (16px) */
    resultsGap: "gap-4",
  },
} as const;

/**
 * Helper to extract spacing value from constant
 * @example
 * getSpacingValue(SPACING.cards.gap) // returns 'gap-4'
 */
export function getSpacingValue(spacing: string): string {
  return spacing;
}

/**
 * Exception: Responsive viewport-based spacing
 * Used when spacing must scale with viewport (e.g., carousel centering)
 *
 * @example
 * // FeaturedHeroRail carousel padding for centering
 * const carouselPadding = 'px-[4vw] sm:px-[4vw] md:px-[15vw] lg:px-[16vw] xl:px-[22.5vw]';
 */
export const SPACING_EXCEPTIONS = {
  /** Carousel horizontal padding for centering cards (viewport-based) */
  carouselPadding: "px-[4vw] sm:px-[4vw] md:px-[15vw] lg:px-[16vw] xl:px-[22.5vw]",
  /** Edge gradient widths matching carousel padding */
  carouselGradient: "w-[4vw] sm:w-[4vw] md:w-[15vw] lg:w-[16vw] xl:w-[22.5vw]",
} as const;
